/**
* @license Apache-2.0
*
* Copyright (c) 2026 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import abs from '@stdlib/math/base/special/abs';
import pow from '@stdlib/math/base/special/pow';
import { ndarray as dcityblock } from './../../../../strided/distances/dcityblock';
import { ndarray as deuclidean } from './../../../../strided/distances/deuclidean';
import { ndarray as dchebyshev } from './../../../../strided/distances/dchebyshev';
import PINF from '@stdlib/constants/float64/pinf';


// MAIN //

/**
* Computes the Minkowski distance between two double-precision floating-point strided arrays using alternative indexing semantics.
*
* @param {NonNegativeInteger} N - number of indexed elements
* @param {number} p - order of the Minkowski norm
* @param {Float64Array} x - first input array
* @param {integer} strideX - stride length of `x`
* @param {NonNegativeInteger} offsetX - starting index for `x`
* @param {Float64Array} y - second input array
* @param {integer} strideY - stride length of `y`
* @param {NonNegativeInteger} offsetY - starting index for `y`
* @returns {number} Minkowski distance
*
* @example
* import Float64Array from '@stdlib/array/float64';
*
* var x = new Float64Array( [ 4.0, 2.0, -3.0, 5.0, -1.0 ] );
* var y = new Float64Array( [ 2.0, 6.0, -1.0, -4.0, 8.0 ] );
*
* var z = dminkowski( x.length, 3, x, 1, 0, y, 1, 0 );
* // returns ~11.543
*/
function dminkowski( N, p, x, strideX, offsetX, y, strideY, offsetY ) {
	var ox;
	var oy;
	var i;
	var d;
	var s;

	if ( N <= 0 ) {
		return NaN;
	}
	if ( p === 1.0 ) {
		return dcityblock( N, x, strideX, offsetX, y, strideY, offsetY );
	}
	if ( p === 2.0 ) {
		return deuclidean( N, x, strideX, offsetX, y, strideY, offsetY );
	}
	if ( p === PINF ) {
		return dchebyshev( N, x, strideX, offsetX, y, strideY, offsetY );
	}
	ox = offsetX;
	oy = offsetY;

	d = 0.0;
	for ( i = 0; i < N; i++ ) {
		s = abs( x[ ox ] - y[ oy ] );
		d += pow( s, p );
		ox += strideX;
		oy += strideY;
	}
	return pow( d, 1.0/p );
}


// EXPORTS //

export default dminkowski;
